import { MetaAdsAccountSetupModalProps, SetupConfiguration, SetupState } from './Types'
import { MetaAdsAccountOverview } from './MetaAdsAccountOverview/MetaAdsAccountOverview'
import React, { useCallback, useMemo, useState } from 'react'
import { MetaAdsAdAccountSelect } from './MetaAdsAdAccountSelect/MetaAdsAdAccountSelect'
import { MetaAdsFacebookSelect } from './MetaAdsFacebookSelect/MetaAdsFacebookSelect'
import { MetaAdsInstagramSelect } from './MetaAdsInstagramSelect/MetaAdsInstagramSelect'
import { useMetaAdsAccounts } from './useMetaAdsAccounts'

type UseMetaAdsAccountSetupOutput = {
  Content: React.ReactNode
  cancelText: string
  isDisabled: boolean
  onModalCancel: () => void
  onModalSubmit: () => void
  title: string
}

export const useMetaAdsAccountSetup = (props: MetaAdsAccountSetupModalProps): UseMetaAdsAccountSetupOutput => {
  const { connectableAccounts, metadata, onCancel, handleSubmit } = props

  const [setupState, setSetupState] = useState<SetupState>(SetupState.AccountOverview)
  const [setupConfiguration, setSetupConfiguration] = useState<SetupConfiguration>({})
  const [configurationUpdates, setConfigurationUpdates] = useState<Partial<SetupConfiguration>>({})

  const { adAccounts, facebookPages, instagramAccounts } = useMetaAdsAccounts(connectableAccounts)

  const selectedAdAccount = useMemo(
    () =>
      setupConfiguration.adAccountId
        ? adAccounts.find((account) => account.provider_id === setupConfiguration.adAccountId)
        : undefined,
    [adAccounts, setupConfiguration.adAccountId],
  )

  const selectedFacebookPage = useMemo(
    () =>
      setupConfiguration.facebookPageId
        ? facebookPages.find((account) => account.provider_id === setupConfiguration.facebookPageId)
        : undefined,
    [facebookPages, setupConfiguration.facebookPageId],
  )

  const selectedInstagramAccount = useMemo(
    () =>
      setupConfiguration.instagramAccountId
        ? instagramAccounts.find((account) => account.provider_id === setupConfiguration.instagramAccountId)
        : undefined,
    [instagramAccounts, setupConfiguration.instagramAccountId],
  )

  const switchToAdAccountSelection = () => {
    setSetupState(SetupState.AdAccountSelection)
  }

  const switchToFacebookPageSelection = () => {
    setSetupState(SetupState.FacebookPageSelection)
  }

  const switchToInstagramAccountSelection = () => {
    setSetupState(SetupState.InstagramAccountSelection)
  }

  const isDisabled = useMemo<boolean>(() => {
    switch (setupState) {
      case SetupState.AdAccountSelection:
        return !configurationUpdates.adAccountId
      case SetupState.FacebookPageSelection:
        return !configurationUpdates.facebookPageId
      case SetupState.InstagramAccountSelection:
        return !configurationUpdates.instagramAccountId
      case SetupState.AccountOverview:
      default:
        return !validateSetupConfiguration(setupConfiguration)
    }
  }, [configurationUpdates, setupState, setupConfiguration])

  const title = useMemo<string>(() => {
    switch (setupState) {
      case SetupState.AdAccountSelection:
        return 'Facebook Ad Account'
      case SetupState.FacebookPageSelection:
        return 'Connect your Facebook Page'
      case SetupState.InstagramAccountSelection:
        return 'Connect your Instagram Account'
      case SetupState.AccountOverview:
      default:
        return 'Meta Advertising'
    }
  }, [setupState])

  const onModalCancel = useCallback(() => {
    if (setupState !== SetupState.AccountOverview) {
      setSetupState(SetupState.AccountOverview)
      setConfigurationUpdates({})
    } else {
      onCancel()
    }
  }, [onCancel, setupState])

  const onModalSubmit = () => {
    if (setupState !== SetupState.AccountOverview) {
      setSetupState(SetupState.AccountOverview)
      setSetupConfiguration((currentSetupConfiguration) => ({
        ...currentSetupConfiguration,
        ...configurationUpdates,
      }))
      setConfigurationUpdates({})
    } else {
      if (!selectedAdAccount || !selectedFacebookPage) return

      const { additional_data, picture, ...account } = selectedAdAccount
      const accountToCreate = {
        ...account,
        picture: picture ?? '',
        additional_data: {
          ...additional_data,
          facebook_account: selectedFacebookPage,
          facebook_page_id: selectedFacebookPage.provider_id,
          instagram_account: selectedInstagramAccount,
          instagram_account_id: selectedInstagramAccount?.provider_id ?? null,
        },
      }

      handleSubmit([accountToCreate])
    }
  }

  const cancelText = useMemo(() => {
    switch (setupState) {
      case SetupState.AccountOverview:
        return 'Cancel'
      default:
        return 'Back'
    }
  }, [setupState])

  const Content = useMemo(() => {
    switch (setupState) {
      case SetupState.AdAccountSelection:
        return (
          <MetaAdsAdAccountSelect
            adAccounts={adAccounts}
            selectedAccountId={configurationUpdates.adAccountId ?? setupConfiguration.adAccountId}
            onSelectAccount={(adAccountId) =>
              setConfigurationUpdates((updates) => ({
                ...updates,
                adAccountId,
              }))
            }
          />
        )
      case SetupState.FacebookPageSelection:
        return (
          <MetaAdsFacebookSelect
            facebookPages={facebookPages}
            selectedAccountId={configurationUpdates.facebookPageId ?? setupConfiguration.facebookPageId}
            onSelectAccount={(facebookPageId) =>
              setConfigurationUpdates((updates) => ({
                ...updates,
                facebookPageId,
              }))
            }
          />
        )
      case SetupState.InstagramAccountSelection:
        return (
          <MetaAdsInstagramSelect
            instagramAccounts={instagramAccounts}
            selectedAccountId={configurationUpdates.instagramAccountId ?? setupConfiguration.instagramAccountId}
            onSelectAccount={(instagramAccountId) =>
              setConfigurationUpdates((updates) => ({
                ...updates,
                instagramAccountId,
              }))
            }
          />
        )
      case SetupState.AccountOverview:
      default:
        return (
          <MetaAdsAccountOverview
            facebookProfile={
              {
                account_type: 'page',
                picture: metadata.profile.picture,
                name: metadata.profile.name,
                provider: 'facebook',
              } as any
            }
            selectedAdAccount={selectedAdAccount}
            selectedFacebookPage={selectedFacebookPage}
            selectedInstagramAccount={selectedInstagramAccount}
            onConnectAdAccount={switchToAdAccountSelection}
            onConnectFacebookPage={switchToFacebookPageSelection}
            onConnectInstagramAccount={switchToInstagramAccountSelection}
          />
        )
    }
  }, [
    adAccounts,
    configurationUpdates,
    facebookPages,
    instagramAccounts,
    metadata,
    selectedAdAccount,
    selectedFacebookPage,
    selectedInstagramAccount,
    setupConfiguration,
    setupState,
  ])

  return {
    Content,
    isDisabled,
    title,
    cancelText,
    onModalCancel,
    onModalSubmit,
  }
}

const validateSetupConfiguration = (setupConfiguration: SetupConfiguration): boolean => {
  return !!setupConfiguration.adAccountId && !!setupConfiguration.facebookPageId
}
